import { observable, computed, action } from 'mobx';
import { CaseEventCategory } from 'types/CaseEventCategory';
import { DefaultEventText } from 'types/DefaultEventText';
import Http from 'services/Http';

export class CaseEventCategoryRow { 
    @observable id: number = 0;
    @observable name: string = '';
    @observable isVisible: boolean = false;
    @observable showOnPortal: boolean = false;
    @observable eventTextID: number = 0;

    @observable eventText: string = ''; 
    @observable eventTextName: string = '';

    @action.bound
    setValue(key: string, val: any) {
        (this as any)[key] = val;
    }

    constructor(cat?: CaseEventCategory) {
        if (cat) {
            this.id = cat.id;
            this.name = cat.name;
            this.isVisible = cat.isVisible;
            this.showOnPortal = cat.showOnPortal;
            this.eventTextID = cat.eventTextID;
        }
    }
}

export class CaseEventCategoryStore {

    @observable categories: CaseEventCategory[] = [];
    @observable eventTexts: DefaultEventText[] = [];
    @observable currentCategory = new CaseEventCategoryRow();

    @computed get eventTextLkp() {
        return new Map<number, DefaultEventText>(this.eventTexts.map(et => [et.id, et]));
    }

    @computed get categoryRows(): CaseEventCategoryRow[] {
        return this.categories.map(c => {
            let row = new CaseEventCategoryRow(c);
            let et = this.eventTextLkp.get(c.eventTextID || -1);
            let eventText = et ? et.defaultValue : '';
            let eventTextName = et ? et.name : '';
            row.eventText = eventText;
            row.eventTextName = eventTextName;
            return row;
        })
    }

    @action.bound
    loadCategories(settings: CaseEventCategory[]) {
        if (settings) this.categories = settings;
    }

    @action.bound
    loadEventTexts(eventTexts: DefaultEventText[]) {
        if (eventTexts) this.eventTexts = eventTexts;
    }

    @action.bound
    async fetchAll() {
        try {
            const res = await Http.get<any>({
                url: 'SystemSettings/GetCaseEventCategories',
                data: {},
                waitMessage: 'Fetching case categories and event text, please wait...',
            });
            if (!res.success) {
                console.error(res.message);
            }
            const eventTexts:DefaultEventText[] = [...res.defaultEventTexts]
            eventTexts.unshift({ id: 0, name: 'Create New Text', defaultValue: '' });
            this.loadCategories(res.caseEventCategories);
            this.loadEventTexts(eventTexts);
        } catch (err) {
            console.error('error fetching CaseEventCategories: ', err);
        }
    }

    @action.bound
    selectRow(row: CaseEventCategoryRow) {
        if (row && row.id === this.currentCategory.id) {
            this.resetCurrentCategory();
        } else {
            const existingRow = this.categoryRows.find(cr => cr.id === row.id) 
            this.currentCategory = existingRow || new CaseEventCategoryRow();
        }
    }

    @action.bound
    resetCurrentCategory() {
        this.currentCategory = new CaseEventCategoryRow();
    }

    @action.bound
    selectExistingEventText(e:any) {
        const selectedID = Number(e.target.value) || 0;
        this.currentCategory.eventTextID = selectedID;
        const et = this.eventTextLkp.get(selectedID);
        this.currentCategory.eventText = et ? et.defaultValue : '';
    }

    @action.bound
    async saveCurrentCategory() {
        await Http.post({ 
            waitMessage: 'Saving case event category, please wait', 
            url: 'SystemSettings/SaveCaseEventCategory', 
            data: this.currentCategory 
        });

        await this.fetchAll();
    }
}

export default new CaseEventCategoryStore()